import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Info, Tab, TabsList, TabsWrapper, Tooltip } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { newFeaturesCampaigns } from "constants/campaigns";
import { IFilters } from "constants/filters";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { hasAnyFilter } from "helpers/filters";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { ITicket, TicketSilo, siloTabNames } from "interfaces/entities/ticket";
import { TicketsViewActions } from "store/views/tickets/actions";
import { getIsFetching, getSilo } from "store/views/tickets/selectors";

interface IProps {
  filters: IFilters;
  searchQuery: string;
  anyDataFetched: boolean;
  data: ITicket[];
}

const TicketsSilos: React.FC<IProps & IWithBreakpointsProps> = ({
  filters,
  searchQuery,
  data,
  anyDataFetched,
  isMobile,
}) => {
  const dispatch = useDispatch();
  const silo = useSelector(getSilo);
  const isFetching = useSelector(getIsFetching);
  const { isNewForAgent, featureFlagValue, setAgentFlagCallback } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenNoResultsInSiloTooltip.flag,
    newFeaturesCampaigns.hasSeenNoResultsInSiloTooltip.campaignStartDate
  );

  const hasSearchOrFilters = hasAnyFilter(filters) || searchQuery.length > 0;

  const shouldDisplayTooltip =
    isNewForAgent &&
    !featureFlagValue &&
    hasSearchOrFilters &&
    anyDataFetched &&
    data.length === 0 &&
    !isMobile &&
    silo === TicketSilo.Tickets &&
    !isFetching;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTabClick = (tab: TicketSilo) => {
    if (silo !== tab) {
      dispatch(TicketsViewActions.setSilo(tab));
      trackEngagement("Search: Silo tab opened", EventPlace.Tickets, {
        tab,
        searchQuery,
      });
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCloseTooltip = () => {
    setAgentFlagCallback();
    trackEngagement("Search: Archives onboarding tooltip closed", EventPlace.Tickets);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const CustomTab = (tab: TicketSilo) => (
    <Tab isSelected={silo === tab} onClick={() => handleTabClick(tab)}>
      {siloTabNames[tab]}
    </Tab>
  );

  return (
    <Block marginTop="10px">
      <TabsWrapper>
        <TabsList>
          {CustomTab(TicketSilo.Tickets)}
          {shouldDisplayTooltip ? (
            <Tooltip
              isVisible
              theme="important"
              triggerRenderer={() => <Block onClick={handleCloseTooltip}>{CustomTab(TicketSilo.Archive)}</Block>}
            >
              <Block width="250px">
                <Info
                  header="Check out other folders"
                  text="If there are no search results, you can look in other folders: Archive, Spam, or Trash."
                  closeWithX
                  handleCloseAction={handleCloseTooltip}
                />
              </Block>
            </Tooltip>
          ) : (
            CustomTab(TicketSilo.Archive)
          )}
          {CustomTab(TicketSilo.Spam)}
          {CustomTab(TicketSilo.Trash)}
        </TabsList>
      </TabsWrapper>
    </Block>
  );
};

export default withBreakpoints(TicketsSilos);
